import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios';

// API error response type
export interface ApiError {
  error: {
    code: string;
    message: string;
    details?: Record<string, unknown>[];
    request_id?: string;
  };
}

// Create axios instance
const apiClient: AxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_URL || '',
  timeout: 30000, // 30 second timeout
});

// Request interceptor - add Firebase auth token
apiClient.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    const { auth } = await import('../lib/firebase');
    const currentUser = auth.currentUser;
    if (currentUser) {
      const token = await currentUser.getIdToken();
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error: AxiosError) => {
    return Promise.reject(error);
  }
);

// Response interceptor - handle errors
apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError<ApiError>) => {
    // Requests cancelled by React Query (navigation, refetch, StrictMode double-mount)
    // are not failures — don't log them as errors or they flood the console.
    if (axios.isCancel(error)) {
      return Promise.reject(error);
    }

    // Extract error message from response
    const errorMessage =
      error.response?.data?.error?.message ||
      error.message ||
      'An unexpected error occurred';

    // Log error for debugging
    console.error('API Error:', {
      url: error.config?.url,
      method: error.config?.method,
      status: error.response?.status,
      message: errorMessage,
      requestId: error.response?.data?.error?.request_id,
    });

    // Handle 401 by signing out and redirecting to login
    if (error.response?.status === 401) {
      const { auth } = await import('../lib/firebase');
      await auth.signOut();
      window.location.href = '/login';
      return Promise.reject(error);
    }

    return Promise.reject(error);
  }
);

// Type-safe API error check
export function isApiError(error: unknown): error is AxiosError<ApiError> {
  return axios.isAxiosError(error) && error.response?.data?.error !== undefined;
}

// Get error message from any error type
export function getErrorMessage(error: unknown): string {
  if (isApiError(error)) {
    return error.response?.data?.error?.message || 'An error occurred';
  }
  if (error instanceof Error) {
    return error.message;
  }
  return 'An unexpected error occurred';
}

export default apiClient;
